import React, { useState, useRef, useEffect } from 'react';
import { Form, Button } from 'react-bootstrap';
import { FiSend, FiCopy, FiCheck, FiChevronLeft, FiChevronRight, FiX, FiPlus, FiMaximize2, FiMinimize2, FiSave, FiEdit3 } from 'react-icons/fi';
import { IoSparkles } from 'react-icons/io5';
import parse from 'html-react-parser';
import styles from './AICompanionPanel.module.scss';

const AICompanionPanel = ({
  aiContent,
  handleInsert,
  handleCopy,
  onClose,
  isGenerating,
  contentGenerated,
  onSendMessage,
  chatMessages = [],
  artifactVersions = [],
  currentArtifactVersion = 0,
  onVersionChange,
  showPanel,
  // New props for expanded mode
  isExpanded = false,
  onToggleExpand,
  onSaveToDocument,
  onReviewInEditor,
  currentEditablePrompt = '',
  onRegeneratePrompt,
}) => {
  const [inputValue, setInputValue] = useState('');
  const [copied, setCopied] = useState(false);
  const [isEditingPrompt, setIsEditingPrompt] = useState(false);
  const [editablePromptText, setEditablePromptText] = useState(currentEditablePrompt);
  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);
  const contentRef = useRef(null);
  const promptTextareaRef = useRef(null);

  // Sync editable prompt with prop
  useEffect(() => {
    setEditablePromptText(currentEditablePrompt);
  }, [currentEditablePrompt]);

  // Auto-scroll chat messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages, isGenerating]);

  // Auto-scroll content as it streams
  useEffect(() => {
    if (contentRef.current && isGenerating) {
      contentRef.current.scrollTop = contentRef.current.scrollHeight;
    }
  }, [aiContent, isGenerating]);

  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = Math.min(textareaRef.current.scrollHeight, 120) + 'px';
    }
  }, [inputValue]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!isGenerating && inputValue.trim() && onSendMessage) {
      onSendMessage(inputValue);
      setInputValue('');
      if (textareaRef.current) {
        textareaRef.current.style.height = 'auto';
      }
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  const handleCopyContent = () => {
    if (handleCopy) {
      handleCopy();
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handlePromptEdit = () => {
    setIsEditingPrompt(true);
    setTimeout(() => {
      promptTextareaRef.current?.focus();
    }, 100);
  };

  const handlePromptSave = () => {
    setIsEditingPrompt(false);
    if (editablePromptText !== currentEditablePrompt && onRegeneratePrompt) {
      onRegeneratePrompt(editablePromptText);
    }
  };

  const handlePromptKeyDown = (e) => {
    if (e.key === 'Enter' && e.ctrlKey) {
      e.preventDefault();
      handlePromptSave();
    }
    if (e.key === 'Escape') {
      setIsEditingPrompt(false);
      setEditablePromptText(currentEditablePrompt);
    }
  };

  const totalVersions = artifactVersions.length;
  const currentVersionDisplay = currentArtifactVersion + 1;

  // Get current content - either streaming or from version history
  const displayContent = isGenerating
    ? aiContent
    : (artifactVersions[currentArtifactVersion]?.content || aiContent);

  if (!showPanel) {
    return null;
  }

  return (
    <div className={`${styles.companion_panel} ${isExpanded ? styles.expanded : ''}`}>
      {/* Header */}
      <div className={styles.panel_header}>
        <div className={styles.header_left}>
          <IoSparkles className={styles.ai_icon} />
          <span className={styles.panel_title}>AI Companion</span>
          {isGenerating && <span className={styles.generating_badge}>Generating...</span>}
        </div>
        <div className={styles.header_actions}>
          {onToggleExpand && (
            <button
              className={styles.expand_btn}
              onClick={onToggleExpand}
              title={isExpanded ? "Collapse panel" : "Expand panel"}
            >
              {isExpanded ? <FiMinimize2 size={16} /> : <FiMaximize2 size={16} />}
            </button>
          )}
          <button className={styles.close_btn} onClick={onClose} title="Close panel">
            <FiX size={18} />
          </button>
        </div>
      </div>

      {/* Live Prompt Section (shown when there's a prompt) */}
      {currentEditablePrompt && (
        <div className={styles.prompt_section}>
          <div className={styles.prompt_header}>
            <span className={styles.prompt_label}>Initial Prompt</span>
            {!isEditingPrompt && (
              <button
                className={styles.edit_prompt_btn}
                onClick={handlePromptEdit}
                disabled={isGenerating}
                title="Edit prompt"
              >
                <FiEdit3 size={14} />
              </button>
            )}
          </div>
          {isEditingPrompt ? (
            <div className={styles.prompt_edit_container}>
              <textarea
                ref={promptTextareaRef}
                value={editablePromptText}
                onChange={(e) => setEditablePromptText(e.target.value)}
                onKeyDown={handlePromptKeyDown}
                className={styles.prompt_textarea}
                rows={3}
              />
              <div className={styles.prompt_edit_actions}>
                <Button
                  size="sm"
                  variant="outline-secondary"
                  onClick={() => {
                    setIsEditingPrompt(false);
                    setEditablePromptText(currentEditablePrompt);
                  }}
                >
                  Cancel
                </Button>
                <Button
                  size="sm"
                  variant="primary"
                  onClick={handlePromptSave}
                  disabled={isGenerating}
                >
                  Regenerate
                </Button>
              </div>
              <div className={styles.prompt_hint}>Ctrl+Enter to regenerate • Esc to cancel</div>
            </div>
          ) : (
            <div className={styles.prompt_display}>
              {currentEditablePrompt}
            </div>
          )}
        </div>
      )}

      {/* Content Area - AI Generated Content */}
      <div className={styles.content_section}>
        <div className={styles.content_header}>
          <h4 className={styles.content_title}>
            {isGenerating && <span className={styles.pulse_dot}></span>}
            {isGenerating ? 'Generating...' : displayContent ? 'Generated Content' : 'Ready to help'}
          </h4>
          {contentGenerated && totalVersions > 0 && (
            <div className={styles.version_selector}>
              <button
                className={styles.version_nav_btn}
                onClick={() => onVersionChange?.(currentArtifactVersion - 1)}
                disabled={currentArtifactVersion <= 0 || isGenerating}
                title="Previous version"
              >
                <FiChevronLeft size={14} />
              </button>
              <span className={styles.version_badge}>v{currentVersionDisplay} of {totalVersions}</span>
              <button
                className={styles.version_nav_btn}
                onClick={() => onVersionChange?.(currentArtifactVersion + 1)}
                disabled={currentArtifactVersion >= totalVersions - 1 || isGenerating}
                title="Next version"
              >
                <FiChevronRight size={14} />
              </button>
            </div>
          )}
        </div>

        <div className={styles.content_display} ref={contentRef}>
          {!displayContent && !isGenerating && (
            <div className={styles.empty_state}>
              <IoSparkles size={40} className={styles.empty_icon} />
              <p>Ask me to edit, expand, or generate content.</p>
              <p className={styles.hint}>Use the input at the bottom of the editor or type below.</p>
            </div>
          )}
          {displayContent && (
            <div className={styles.content_text}>
              {parse(displayContent)}
            </div>
          )}
          {isGenerating && !displayContent && (
            <div className={styles.loading_state}>
              <div className={styles.loading_spinner}></div>
              <p>Thinking...</p>
            </div>
          )}
        </div>

        {/* Content Actions */}
        {contentGenerated && displayContent && (
          <div className={styles.content_actions}>
            <Button
              size="sm"
              variant=""
              className={styles.action_btn}
              onClick={handleCopyContent}
              title={copied ? "Copied!" : "Copy to clipboard"}
            >
              {copied ? <FiCheck size={14} /> : <FiCopy size={14} />}
              <span>{copied ? 'Copied' : 'Copy'}</span>
            </Button>
            {onSaveToDocument && (
              <Button
                size="sm"
                variant=""
                className={styles.action_btn}
                onClick={onSaveToDocument}
                disabled={!contentGenerated}
                title="Save directly to document"
              >
                <FiSave size={14} />
                <span>Save</span>
              </Button>
            )}
            <Button
              size="sm"
              variant=""
              className={styles.primary_btn}
              onClick={onReviewInEditor || handleInsert}
              disabled={!contentGenerated}
            >
              <FiPlus size={14} />
              <span>Insert</span>
            </Button>
          </div>
        )}
      </div>

      {/* Chat Messages Area */}
      {chatMessages.length > 0 && (
        <div className={styles.messages_section}>
          <div className={styles.messages_header}>
            <span>Conversation</span>
          </div>
          <div className={styles.messages_list}>
            {chatMessages.map((message) => (
              <div
                key={message.id}
                className={`${styles.message} ${styles[message.type]}`}
              >
                <div className={styles.message_content}>{message.content}</div>
              </div>
            ))}
            {isGenerating && (
              <div className={`${styles.message} ${styles.activity}`}>
                <span className={styles.activity_dot}></span>
                <span>AI is working...</span>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>
        </div>
      )}

      {/* Chat Input Area */}
      <div className={styles.input_section}>
        <Form onSubmit={handleSubmit} className={styles.input_form}>
          <div className={styles.input_wrapper}>
            <textarea
              ref={textareaRef}
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={isGenerating ? 'Generating...' : 'Ask AI to edit, expand, or refine...'}
              className={styles.chat_input}
              disabled={isGenerating}
              rows={1}
            />
            <Button
              type="submit"
              className={styles.send_btn}
              disabled={isGenerating || !inputValue.trim()}
              variant=""
            >
              <FiSend size={16} />
            </Button>
          </div>
          <div className={styles.input_hint}>
            Press Enter to send • Shift + Enter for new line
          </div>
        </Form>
      </div>
    </div>
  );
};

export default AICompanionPanel;
